import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Errored from './Errored';
import ActionText from './ActionText';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Tooltip from '@material-ui/core/Tooltip';
import jsonld from 'jsonld';
import PropTypes from 'prop-types';
import React from 'react';
import GS from '../grafsync/grafsync';
import Loading from './Loading';
import { globalStyles as styles } from './style/gstheme';
import { makeCancelable } from './utils/utils';
import namespaces from './utils/namespaces';
import withGS from './withGShoc';

const namespaceKeys = Object.keys(namespaces);

class TripleViewer extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            triples: null,
            tooManyTriples: false,
            toobig: false,
            isErrored: false,
            failed: false
        };
        this.cancellable = null;
    }

    static getDerivedStateFromProps(props, state) {
        // We use the object's internal version counter set by GS to know if changed
        if (props.data._version !== state._version) {
            return {
                triples: null,
                _version: props.data._version,
            };
        }
        return null;
    }

    componentDidMount() {
        if (!this.props.data._notAvailable) {
            this.convertJSONtoTriples(this.props.data);
        }
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    componentDidUpdate() {
        if (this.state.isErrored === false && this.state.triples === null && !this.props.data._notAvailable) {
            this.convertJSONtoTriples(this.props.data);
        }
    }

    // As converting if probably quite unusual we do it here rather than GS supporting the conversion (for now anyway)
    convertJSONtoTriples(contextData) {
        try {
            const props = Object.keys(contextData);
            const massive = props.some(prop => Array.isArray(contextData[prop]) && contextData[prop].length > 10000);
            if (massive) {
                this.setState({ toobig: true, triples: [] });
                return;
            }
            const context = GS.getFrame('applicationFrame');
            contextData["@context"] = context["@context"];
            delete contextData.hasEntitlement;
            delete contextData.canUpdate;
            delete contextData.targetScope;
            delete contextData.isTargetScope;
            delete contextData.iri;
            jsonld.toRDF(contextData, { format: 'application/n-quads' })
                .then(data => {
                    const lines = data.split(".\n");
                    const po = lines
                        .filter(line => line.length > 1)
                        .map(line => {
                            const subjectLength = line.indexOf(" ") + 1;
                            const poText = line.substring(subjectLength).split("> ");
                            const p = poText[0].substring(1);
                            let o = poText[1];
                            const d = o.indexOf('"^^') > 0 ? o.substring(o.indexOf('"^^') + 4) : "";
                            let l = o.indexOf('"@') > 0 ? o.substring(o.indexOf('"@') + 2).trim() : "";
                            if (l === "und") l = "No language";
                            o = o.indexOf('"') === 0 && o.indexOf('"^^') > 0 ? o.substring(1, o.indexOf('"^^')) : o;
                            o = o.indexOf('"') === 0 && o.indexOf('"@') > 0 ? o.substring(1, o.indexOf('"@')) : o;
                            // Plain literal
                            o = o.indexOf('"') === 0 ? o.substring(1, o.length - 2) : o;
                            o = o.indexOf('<') === 0 ? o.substring(1) : o;
                            if (d !== "") {
                                o = o.endsWith('" ') ? o.substring(0, o.length - 2) : o;
                            }
                            return {
                                p,
                                o,
                                d,
                                l
                            };
                        });
                    let tooManyTriples = false;
                    let triples = po.sort();
                    const tripleCount = triples.length;
                    if (tripleCount > 1000) {
                        triples = triples.slice(0, 1000);
                        tooManyTriples = true;
                    }
                    const irisToLoad = [...new Set(triples.filter(triple => triple.o.indexOf('http') === 0).map(triple => triple.o))];
                    const graph = this.props.graph;
                    this.loadData(irisToLoad, {
                        predicates: [
                            "http://www.w3.org/2004/02/skos/core#narrower",
                            "https://graphifi.com/schema/xowl/hasSubAnnotationProperty",
                            "https://grafsync.graphifi.com/schema/gs/iri"
                        ]
                    }, graph)
                        .promise
                        .then(() => {
                            if (this.cancellable && this.cancellable.isCancelled()) return;
                            triples.forEach(triple => {
                                // Map IRI changes
                                if (triple.o.indexOf('http') === 0 && GS.isLoaded(triple.o, graph)) {
                                    const data = GS.data(triple.o, graph);
                                    if (data.iri && data.iri.length > 0) triple.o = data.iri[data.iri.length - 1];
                                }
                            });
                            this.setState({ triples, tooManyTriples, tripleCount });
                        })
                        .catch(e => {
                            if (this.cancellable && this.cancellable.isCancelled()) return;
                            this.setState({ isErrored: e });
                        });
                })
                .catch(e => {
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    this.setState({ isErrored: e });
                });
        }
        catch (e) {
            if (this.cancellable && this.cancellable.isCancelled()) return;
            this.setState({ isErrored: e });
        }
    }

    loadData(iris, options, graph) {
        this.cancellable = makeCancelable(GS.loadData(iris, options, graph), 'TRIPLE_VIEWER');
        return this.cancellable;
    }

    checkForPrefix(iri) {
        const ns = namespaceKeys.find(prefix => iri && iri.startsWith(prefix) ? true : false);
        return ns ? namespaces[ns] + ":" + iri.substring(ns.length) : iri;
    }

    clearError = () => this.setState({ isErrored: false, triples: null, failed: true });

    unescapeText = function (text) {
        const regexQuote = /\\u0022/g;
        const regexBackSlash = /\\u005C/g;
        const regexTab = /\t/g;
        const regexDoubleQuote = /\\"/g;
        text = text.replace(regexDoubleQuote, '"');
        text = text.replace(regexQuote, '"');
        text = text.replace(regexBackSlash, '\\');
        text = text.replace(regexTab, '\t');
        // Cater for actual \n and newline and \ in a string!
        text = text.replace(/(\\\\n)|(\\n)|(\\\\)/g, function (match) {
            return match === "\\\\n" ?
                "\\n"
                :
                match === "\\n" ?
                    "\n"
                    :
                    "\\";
        });
        return text;
    };

    removeTriple = (triple) => {
        try {
            const lang = triple.l && triple.l !== "" ? triple.l : null;
            const datatype = triple.d && triple.d !== "" ? triple.d : null;
            const value = this.unescapeText(triple.o);
            GS.deleteTriple(this.props.id, this.props.graph, triple.p, value, lang, datatype);
        }
        catch (e) {
            this.setState({ isErrored: e });
        }
    };

    render() {

        const { classes, displayTitle, data } = this.props;
        const { triples, tooManyTriples, tripleCount, isErrored, failed, toobig } = this.state;

        if (failed) return <ActionText bottom="100px" offset="130px">Triple view cannot be generated.</ActionText>
        if (isErrored) return <Errored data={isErrored} clearError={this.clearError} />;
        if (toobig) return <ActionText bottom="100px" offset="130px">Too much data to generate triple view.</ActionText>
        if (data._notAvailable) return <ActionText bottom="100px" offset="130px">Triple view is not available.</ActionText>;
        if (triples === null) return <Loading minHeight="140px" offset="100px" />;

        return (
            <div style={!displayTitle ? { margin: '8px', paddingBottom: 24 } : { paddingBottom: 24 }}>
                {displayTitle && <Typography paragraph className={classes.mainHeading} style={{ marginTop: 10 }}>Triples</Typography>}
                {tooManyTriples &&
                    <Typography data-test="onlyfirst" paragraph variant="body2">Displaying only the first 1000 (of {tripleCount}) triples.</Typography>
                }
                <Table data-test="triple-viewer" style={{ tableLayout: 'fixed' }} size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell colSpan={3} component="th" className={classes.tableHeading}>Predicate</TableCell>
                            <TableCell colSpan={3} component="th" className={classes.tableHeading}>Object</TableCell>
                            <TableCell component="th" className={classes.tableHeading}>Datatype or Language</TableCell>
                            <TableCell component="th" className={classes.tableHeading} align="right">Delete</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {triples.map((triple, index) => {
                            const alias = GS.getAlias(triple.p);
                            const hasAlias = alias && !alias.startsWith("https://") && !alias.startsWith("http://");
                            return <TableRow
                                data-test={"row-" + triple.p}
                                key={"row-" + index}
                                classes={{
                                    root: classes.stripedRow
                                }}>
                                <TableCell colSpan={3} className={classes.tableCell}>
                                    <Tooltip placement="bottom-start" title={triple.p} aria-label={triple.p}>
                                        <Typography className={classes.tableCellText}>{this.checkForPrefix(triple.p)}</Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell colSpan={3} className={classes.tableCell}>
                                    <Tooltip placement="bottom-start" title={triple.o} aria-label={triple.o}>
                                        <Typography className={classes.tableCellText}>{this.checkForPrefix(triple.o)}</Typography>
                                    </Tooltip>
                                </TableCell>
                                <TableCell className={classes.tableCell}>
                                    {triple.d &&
                                        <Tooltip placement="bottom-start" title={triple.d} aria-label={triple.d}>
                                            <Typography className={classes.tableCellText}>{this.checkForPrefix(triple.d)}</Typography>
                                        </Tooltip>
                                    }
                                    {triple.l &&
                                        <Typography className={classes.tableCellText}>{triple.l}</Typography>
                                    }
                                </TableCell>
                                <TableCell className={classes.tableCell} align="right" style={{ maxWidth: 40 }}>
                                    {!hasAlias &&
                                        <IconButton data-test="delete-triple" size="small" onClick={() => this.removeTriple(triple)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    }
                                </TableCell>
                            </TableRow>;
                        })}
                    </TableBody>
                </Table>
            </div>
        );
    }
}

TripleViewer.defaultProps = {
    displayTitle: true
};

TripleViewer.propTypes = {
    data: PropTypes.object.isRequired,
    graph: PropTypes.string.isRequired,
    displayTitle: PropTypes.bool,
    classes: PropTypes.object
};

export default withStyles(styles)(withGS(TripleViewer));